import { useCallback, useEffect, useState } from "react";

import { Flyout } from "@bokio/components/Flyout/Flyout";
import { AppMessageType, useAppContext } from "@bokio/contexts/AppContext/AppContext";
import { CloseButton } from "@bokio/designsystem/components/CloseButton/CloseButton";
import Icon from "@bokio/designsystem/components/Icon/Icon";
import { Heading, Section } from "@bokio/designsystem/components/TypographicElements/TypographicElements";
import { Link } from "@bokio/elements/Link/Link";
import { GeneralLangFactory } from "@bokio/lang";
import { useUmbracoApi } from "@bokio/shared/services/api/UmbracoApi";
import { useAgencyStatus } from "@bokio/shared/state/requests";

import type { UmbracoReleaseNoteModel } from "@bokio/shared/models/UmbracoReleaseNotesModel";

import * as styles from "./whatsNewFlyout.scss";

interface WhatsNewFlyoutProps {
	whatsNewData: UmbracoReleaseNoteModel[];
	isOpen: boolean;
	onClose: () => void;
}

export const WhatsNewFlyout = ({ isOpen, onClose, whatsNewData }: WhatsNewFlyoutProps) => {
	const generalLang = GeneralLangFactory();
	const app = useAppContext();
	const { agencyStatus } = useAgencyStatus();
	const { getReleaseNotesAllEmbedUrl } = useUmbracoApi();
	const [iframeHeight, setIframeHeight] = useState<number | undefined>(undefined);

	const MenuWhatsNew_ReleaseNotesLink = () => {
		if (agencyStatus !== undefined) {
			if (whatsNewData.length === 0) {
				return generalLang.MenuWhatsNew_ReleaseNotesLink;
			}
			return generalLang.MenuWhatsNew_ReleaseNotesLink_Agency;
		}
		return generalLang.MenuWhatsNew_ReleaseNotesLink_Company;
	};

	const expectedOrigin = new URL(getReleaseNotesAllEmbedUrl()).origin;

	const handleIframeMessage = useCallback(
		(event: MessageEvent) => {
			if (event.origin !== expectedOrigin) {
				return;
			}
			if (event.data?.type !== "releaseNoteEmbedResize") {
				return;
			}
			setIframeHeight(event.data.height);
		},
		[expectedOrigin],
	);

	useEffect(() => {
		window.addEventListener("message", handleIframeMessage);
		return () => window.removeEventListener("message", handleIframeMessage);
	}, [handleIframeMessage]);

	useEffect(() => {
		if (isOpen && app && app.messages.length > 0) {
			const agencyReleaseUpdates = app.messages.filter(m => m.type === AppMessageType.ReleaseNoteUpdatesInAgency);
			if (agencyReleaseUpdates && agencyReleaseUpdates.length > 0) {
				app.dismissMessage(agencyReleaseUpdates[0].key);
			}
		}
	}, [app, isOpen]);

	return (
		<Flyout
			asideClassName={styles.flyoutAside}
			backgroundClassName={styles.flyoutBackground}
			isOpen={isOpen}
			onClickOutside={onClose}
		>
			<Section>
				<div className={styles.header}>
					<CloseButton onClick={onClose} />
					<div className={styles.headerText}>
						<Heading>{generalLang.MenuWhatsNew_label}</Heading>
					</div>
					<Link external={MenuWhatsNew_ReleaseNotesLink()} target="_blank">
						<Icon name="external-link" size="18" />
					</Link>
				</div>
			</Section>

			<div className={styles.scrollable}>
				{/*
				 * Sandbox notes:
				 * - allow-same-origin: needed so the iframe can load fonts and other
				 *   same-origin resources. Safe because iframe (PublicWeb) and parent
				 *   (Voder) are cross-origin, so it cannot be used to escape the sandbox.
				 * - allow-scripts: needed for height reporting via postMessage
				 * - allow-popups: so links open in new tabs
				 * - allow-popups-to-escape-sandbox: so opened tabs work normally
				 */}
				<iframe
					src={getReleaseNotesAllEmbedUrl()}
					className={styles.iframe}
					style={iframeHeight ? { height: `${iframeHeight}px` } : undefined}
					title={generalLang.MenuWhatsNew_label}
					// Safe: iframe (PublicWeb) and parent (Voder) are cross-origin, so allow-same-origin cannot be used to escape the sandbox.
					// eslint-disable-next-line @eslint-react/dom-no-unsafe-iframe-sandbox
					sandbox="allow-same-origin allow-scripts allow-popups allow-popups-to-escape-sandbox"
				/>
			</div>
		</Flyout>
	);
};
